import { useClient } from '@cortex/react-query';
import type { Suite, Task, WorkflowListItem } from '@cortex/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import { BackButton } from '../../components/common/BackButton';
import { InfoHint } from '../../components/common/InfoHint';
import { PageHeader } from '../../components/common/PageHeader';
import { useDocumentSets } from '../../hooks/useDocumentSets';
import { routes } from '../../lib/constants';

export const Route = createFileRoute('/suites/new')({ component: NewSuitePage });

interface IListEnvelope<T> {
  items: T[];
  total: number;
}

const stack3: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--sp-3)',
};

function NewSuitePage() {
  const client = useClient();
  const qc = useQueryClient();
  const nav = useNavigate();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [workflowId, setWorkflowId] = useState('');
  const [targetKind, setTargetKind] = useState<'WORKFLOW' | 'KG'>('WORKFLOW');
  const [kind, setKind] = useState<'CAPABILITY' | 'REGRESSION'>('CAPABILITY');
  const [gatesRun, setGatesRun] = useState(false);
  const [expectedPassRate, setExpectedPassRate] = useState('');
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);
  const [taskSearch, setTaskSearch] = useState('');
  const [taskPickerOpen, setTaskPickerOpen] = useState(false);

  const workflows = useQuery({
    queryKey: ['workflows', 'for-suite'],
    queryFn: () =>
      client.http.get('v1/workflows?limit=200').json<IListEnvelope<WorkflowListItem>>(),
  });

  const docSets = useDocumentSets();

  const tasks = useQuery({
    queryKey: ['tasks', 'all-for-suite'],
    queryFn: () => client.http.get('v1/tasks?limit=200').json<IListEnvelope<Task>>(),
  });

  const taskList = tasks.data?.items ?? [];
  // Only surface tasks whose target matches this suite — mixing WORKFLOW
  // tasks into a KG suite (or vice-versa) doesn't run and confuses the picker.
  const taskListForTarget = taskList.filter((t) => (t.targetKind ?? 'WORKFLOW') === targetKind);
  const availableTasks = taskListForTarget.filter((t) => !selectedTaskIds.includes(t.id));
  const selectedTasks = taskListForTarget.filter((t) => selectedTaskIds.includes(t.id));
  const filteredTasks = taskSearch
    ? availableTasks.filter(
        (t) =>
          t.name.toLowerCase().includes(taskSearch.toLowerCase()) ||
          (t.description ?? '').toLowerCase().includes(taskSearch.toLowerCase()),
      )
    : availableTasks;

  const canSubmit = name.trim().length > 0 && selectedTaskIds.length > 0;

  const create = useMutation({
    mutationFn: () =>
      client.http
        .post('v1/suites', {
          json: {
            name: name.trim(),
            description: description.trim() || undefined,
            targetKind,
            workflowId: targetKind === 'WORKFLOW' ? workflowId || null : null,
            kind,
            gatesRun,
            expectedPassRate: expectedPassRate ? Number(expectedPassRate) : undefined,
            taskIds: selectedTaskIds,
          },
        })
        .json<Suite>(),
    onSuccess: async (s) => {
      await qc.invalidateQueries({ queryKey: ['suites'] });
      await nav({ to: routes.suiteDetail(s.id) });
    },
  });

  return (
    <>
      <PageHeader title="New Suite" actions={<BackButton to={routes.SUITES} label="Suites" />} />
      <div style={{ padding: '0 36px 60px', maxWidth: 700 }}>
        <div className="card">
          <div className="card-body">
            <form
              style={stack3}
              onSubmit={(e) => {
                e.preventDefault();
                if (canSubmit && !create.isPending) create.mutate();
              }}
            >
              {/* Name */}
              <label className="field">
                <span className="field-label">Name</span>
                <input
                  type="text"
                  className="input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Intel alarm regression"
                  maxLength={200}
                  required={true}
                />
              </label>

              {/* Description */}
              <label className="field">
                <span className="field-label">Description (optional)</span>
                <textarea
                  className="textarea"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="What this suite measures and when tasks should be added to it"
                  maxLength={2000}
                  rows={3}
                />
              </label>

              {/* Target — WORKFLOW suites hold workflow-target tasks;
                  KG suites hold KG-target tasks. Locked at creation because
                  it constrains which tasks may join. */}
              <div className="field">
                <span className="field-label">
                  Target
                  <InfoHint
                    text="What kind of task this suite groups. WORKFLOW suites run against a workflow; KG suites run doc-set KG queries. Only same-target tasks can join."
                    side="right"
                  />
                </span>
                <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
                  {(
                    [
                      ['WORKFLOW', 'Workflow'],
                      ['KG', 'Knowledge Graph'],
                    ] as const
                  ).map(([t, label]) => (
                    <label
                      key={t}
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: 'var(--sp-2) var(--sp-3)',
                        border: `1px solid ${targetKind === t ? 'var(--accent-primary)' : 'var(--border-subtle)'}`,
                        borderRadius: 'var(--radius-sm)',
                        background: targetKind === t ? 'var(--surface-2)' : 'transparent',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="radio"
                        name="targetKind"
                        value={t}
                        checked={targetKind === t}
                        onChange={() => {
                          setTargetKind(t);
                          // Clear selections when switching — tasks are target-scoped.
                          setSelectedTaskIds([]);
                          if (t === 'KG') setWorkflowId('');
                        }}
                        style={{ margin: 0 }}
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Workflow — only for WORKFLOW target. Advisory scope hint. */}
              {targetKind === 'WORKFLOW' && (
                <label className="field">
                  <span className="field-label">Workflow (optional)</span>
                  <select
                    className="input"
                    value={workflowId}
                    onChange={(e) => setWorkflowId(e.target.value)}
                  >
                    <option value="">Not specified</option>
                    {(workflows.data?.items ?? []).map((w) => (
                      <option key={w.id} value={w.id}>
                        {w.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              {/* KG suites don't pin a doc-set — each task carries its own.
                  We just show a helper hint if there are no KG tasks yet. */}
              {targetKind === 'KG' && (docSets.data?.length ?? 0) === 0 && (
                <div
                  style={{
                    padding: 'var(--sp-3)',
                    background: 'var(--surface-2)',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: 12,
                    color: 'var(--text-muted)',
                  }}
                >
                  No document sets exist yet. Create one from the Document Sets page before
                  authoring KG tasks.
                </div>
              )}

              {/* Kind */}
              <div className="field">
                <span className="field-label">
                  Kind
                  <InfoHint
                    text={
                      <>
                        <b>Capability:</b> measuring new abilities. Pass rate is typically low or
                        climbing.
                        <br />
                        <br />
                        <b>Regression:</b> guarding against accuracy drops. Pass rate should be
                        ~100%. Can gate deploys when combined with &ldquo;Gates Run&rdquo;.
                      </>
                    }
                    side="right"
                  />
                </span>
                <div style={{ display: 'flex', gap: 'var(--sp-2)' }}>
                  {(['CAPABILITY', 'REGRESSION'] as const).map((k) => (
                    <label
                      key={k}
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: 'var(--sp-2) var(--sp-3)',
                        border: `1px solid ${kind === k ? 'var(--accent-primary)' : 'var(--border-subtle)'}`,
                        borderRadius: 'var(--radius-sm)',
                        background: kind === k ? 'var(--surface-2)' : 'transparent',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="radio"
                        name="kind"
                        value={k}
                        checked={kind === k}
                        onChange={() => {
                          setKind(k);
                          if (k === 'CAPABILITY') {
                            setGatesRun(false);
                            setExpectedPassRate('');
                          }
                        }}
                        style={{ margin: 0 }}
                      />
                      <span
                        className="badge"
                        style={{
                          background:
                            k === 'REGRESSION'
                              ? 'var(--status-danger-bg)'
                              : 'var(--status-info-bg)',
                          color: k === 'REGRESSION' ? 'var(--status-danger)' : 'var(--status-info)',
                          border: `1px solid ${k === 'REGRESSION' ? 'var(--status-danger-bd)' : 'var(--status-info-bd)'}`,
                          fontSize: 11,
                        }}
                      >
                        {k}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Regression-specific fields */}
              {kind === 'REGRESSION' ? (
                <div
                  style={{
                    padding: 'var(--sp-3)',
                    background: 'var(--surface-2)',
                    borderRadius: 'var(--radius-sm)',
                    ...stack3,
                  }}
                >
                  <label
                    className="field"
                    style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}
                  >
                    <input
                      type="checkbox"
                      checked={gatesRun}
                      onChange={(e) => setGatesRun(e.target.checked)}
                    />
                    <span className="field-label" style={{ margin: 0 }}>
                      Gates Run
                      <InfoHint
                        text="When enabled, the eval run fails if any Task's pass rate drops below the expected baseline. Used to block deploys."
                        side="right"
                      />
                    </span>
                  </label>
                  <label className="field">
                    <span className="field-label">
                      Expected Pass Rate
                      <InfoHint
                        text="The minimum pass rate threshold (0-1). Tasks must meet this rate for the gate to pass. E.g. 0.95 means 95% of tasks must pass."
                        side="right"
                      />
                    </span>
                    <input
                      type="number"
                      className="input"
                      min={0}
                      max={1}
                      step={0.01}
                      value={expectedPassRate}
                      onChange={(e) => setExpectedPassRate(e.target.value)}
                      placeholder="0.95"
                      style={{ maxWidth: 120 }}
                    />
                  </label>
                </div>
              ) : null}

              {/* Initial tasks */}
              <div className="field">
                <span className="field-label">
                  Tasks
                  <InfoHint
                    text="A suite needs at least one task. Select the tasks this suite will evaluate."
                    side="right"
                  />
                </span>

                {/* Selected chips */}
                {selectedTasks.length > 0 ? (
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 6,
                      marginBottom: 'var(--sp-2)',
                    }}
                  >
                    {selectedTasks.map((t) => (
                      <span
                        key={t.id}
                        className="badge"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '4px 10px',
                          fontSize: 12,
                          background: 'var(--surface-2)',
                          border: '1px solid var(--border-subtle)',
                          borderRadius: 'var(--radius-sm)',
                        }}
                      >
                        {t.name}
                        <button
                          type="button"
                          onClick={() =>
                            setSelectedTaskIds((prev) => prev.filter((id) => id !== t.id))
                          }
                          style={{
                            border: 'none',
                            background: 'none',
                            color: 'var(--text-muted)',
                            cursor: 'pointer',
                            padding: 0,
                            fontSize: 13,
                            lineHeight: 1,
                          }}
                        >
                          x
                        </button>
                      </span>
                    ))}
                  </div>
                ) : null}

                {/* Searchable picker */}
                <div
                  style={{ position: 'relative' }}
                  onBlur={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                      setTimeout(() => setTaskPickerOpen(false), 150);
                    }
                  }}
                >
                  <input
                    type="text"
                    className="input"
                    placeholder={
                      tasks.isLoading
                        ? 'Loading tasks...'
                        : availableTasks.length === 0 && selectedTaskIds.length > 0
                          ? 'All tasks selected'
                          : 'Search or click to browse tasks...'
                    }
                    value={taskSearch}
                    onChange={(e) => {
                      setTaskSearch(e.target.value);
                      setTaskPickerOpen(true);
                    }}
                    onFocus={() => setTaskPickerOpen(true)}
                    disabled={
                      tasks.isLoading ||
                      (availableTasks.length === 0 && selectedTaskIds.length === 0)
                    }
                  />
                  {taskPickerOpen && filteredTasks.length > 0 ? (
                    <div
                      style={{
                        border: '1px solid var(--border-subtle)',
                        borderRadius: 'var(--radius-sm)',
                        maxHeight: 200,
                        overflowY: 'auto',
                        background: 'var(--surface-1)',
                      }}
                    >
                      {filteredTasks.map((t) => (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => {
                            setSelectedTaskIds((prev) => [...prev, t.id]);
                            setTaskSearch('');
                            setTaskPickerOpen(false);
                          }}
                          style={{
                            display: 'block',
                            width: '100%',
                            textAlign: 'left',
                            padding: '8px 12px',
                            border: 'none',
                            background: 'none',
                            color: 'var(--text-primary)',
                            cursor: 'pointer',
                            fontSize: 13,
                            borderBottom: '1px solid var(--border-subtle)',
                          }}
                          onMouseEnter={(e) => {
                            (e.target as HTMLElement).style.background = 'var(--surface-2)';
                          }}
                          onMouseLeave={(e) => {
                            (e.target as HTMLElement).style.background = 'none';
                          }}
                        >
                          {t.name}
                          {t.description ? (
                            <span className="t-muted" style={{ marginLeft: 8, fontSize: 11 }}>
                              {t.description.slice(0, 60)}
                            </span>
                          ) : null}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
                <span
                  className="t-xs"
                  style={{
                    marginTop: 'var(--sp-1)',
                    color:
                      selectedTaskIds.length === 0 ? 'var(--status-danger)' : 'var(--text-muted)',
                  }}
                >
                  {selectedTaskIds.length === 0
                    ? 'At least one task is required.'
                    : `${selectedTaskIds.length} ${selectedTaskIds.length === 1 ? 'task' : 'tasks'} selected`}
                </span>
              </div>

              {/* Submit */}
              <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' }}>
                <button
                  type="submit"
                  className="btn btn--primary"
                  disabled={!canSubmit || create.isPending}
                >
                  {create.isPending ? 'Creating...' : 'Create suite'}
                </button>
                {create.error ? (
                  <span className="t-xs" style={{ color: 'var(--status-danger)' }}>
                    {(create.error as Error).message}
                  </span>
                ) : null}
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
